/** A section layout and field references must both allow a field before it is shown. */
export function syncFieldGroupVisibility(group) {
    group.hidden = group.dataset.layoutVisible === 'false'
        || group.dataset.dependencyVisible === 'false';
}

/**
 * Controls that depend on another field: a `dependsOn` field shows only while the field it
 * names has a value. Binding is idempotent per field group, so the page form can call
 * {@link sync} after every render or prefill.
 */
export class FieldReferenceControls {
    #root;
    #manageRequired;
    /** Re-sync callbacks of bound controls, keyed by field group. */
    #syncByGroup = new WeakMap();

    /**
     * @param {ShadowRoot} root
     * @param {{manageRequired?: boolean}} [options] `manageRequired: false` leaves the native
     *   `required` flag of dependent fields alone, for editors that enforce required rules
     *   themselves
     */
    constructor(root, {manageRequired = true} = {}) {
        this.#root = root;
        this.#manageRequired = manageRequired;
    }

    #$(selector) {
        return this.#root.querySelector(selector);
    }

    /**
     * Binds the `dependsOn` controls of the rendered fields, or re-syncs the ones already
     * bound (for example after prefilled values were written).
     * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
     */
    sync(fields) {
        for (const field of fields) {
            if (field.dependsOn) this.#bindDependent(field);
        }
    }

    /** Shows a `dependsOn` field only while the field it names has a value. */
    #bindDependent(field) {
        const group = this.#$(`.field-group[data-field-key="${field.key}"]`);
        const source = this.#$(`[name="${field.dependsOn}"]`);
        if (!group || !source) return;
        const existingSync = this.#syncByGroup.get(group);
        if (existingSync) {
            existingSync();
            return;
        }
        const input = group.querySelector(`[name="${field.key}"]`);
        const sync = () => {
            group.dataset.dependencyVisible = String(Boolean(source.value.trim()));
            syncFieldGroupVisibility(group);
            if (input && this.#manageRequired && !group.closest('.section-card')) input.required = Boolean(field.required && !group.hidden);
        };
        source.addEventListener('input', sync);
        source.addEventListener('change', sync);
        this.#syncByGroup.set(group, sync);
        sync();
    }
}
